import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plug, Plus, Trash2, X as XIcon, CircleDot, Sparkles } from "lucide-react";
import { PageHeader, PageBody } from "../components/PageScaffold";
import { Panel, Button, Pill, CenteredSpinner, ErrorBanner, EmptyState } from "../components/primitives";
import { McpBuilder } from "../components/McpBuilder";
import { BridgeApi } from "../lib/api";
import { qk } from "../lib/query";
import { cn } from "../lib/cn";
import { useToast } from "../lib/toast";
import type { McpServerOverview, McpGrantMode } from "../lib/types";

// Known remote MCP connectors the gateway can connect to, end-to-end. Each was
// verified by running real dynamic client registration against the server.
//   auth "oauth"          → click Connect (open DCR accepts our server callback)
//   auth "oauth" + manual → click "manually" (provider allowlists redirect URIs
//                           to loopback, so use the loopback + paste-code flow)
//   auth "none"           → public, connects immediately
// (HubSpot is absent: its AS doesn't support DCR at all, so neither path works —
// it would need a manually-created static OAuth app.)
const CONNECTOR_CATALOG: { label: string; name: string; url: string; auth: "oauth" | "none"; manual?: boolean }[] = [
  { label: "Notion", name: "notion", url: "https://mcp.notion.com/mcp", auth: "oauth" },
  { label: "Linear", name: "linear", url: "https://mcp.linear.app/mcp", auth: "oauth" },
  { label: "Airtable", name: "airtable", url: "https://mcp.airtable.com/mcp", auth: "oauth" },
  { label: "Sentry", name: "sentry", url: "https://mcp.sentry.dev/mcp", auth: "oauth" },
  { label: "Canva", name: "canva", url: "https://mcp.canva.com/mcp", auth: "oauth" },
  { label: "PayPal", name: "paypal", url: "https://mcp.paypal.com/mcp", auth: "oauth" },
  { label: "Webflow", name: "webflow", url: "https://mcp.webflow.com/mcp", auth: "oauth" },
  { label: "Composio", name: "composio", url: "https://connect.composio.dev/mcp", auth: "oauth" },
  { label: "Asana", name: "asana", url: "https://mcp.asana.com/mcp", auth: "oauth", manual: true },
  { label: "Vercel", name: "vercel", url: "https://mcp.vercel.com", auth: "oauth", manual: true },
  { label: "Square", name: "square", url: "https://mcp.squareup.com/mcp", auth: "oauth", manual: true },
  { label: "Cloudflare Docs (public)", name: "cloudflare-docs", url: "https://docs.mcp.cloudflare.com/mcp", auth: "none" },
  { label: "Hugging Face (public)", name: "huggingface", url: "https://huggingface.co/mcp", auth: "none" },
  { label: "DeepWiki (public)", name: "deepwiki", url: "https://mcp.deepwiki.com/mcp", auth: "none" },
];

export default function Mcp() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({ queryKey: qk.mcp.overview, queryFn: () => BridgeApi.mcpOverview(), refetchInterval: 15_000 });
  const [adding, setAdding] = useState(false);
  const [building, setBuilding] = useState(false);

  return (
    <>
      {building && <McpBuilder onClose={() => setBuilding(false)} onImported={() => qc.invalidateQueries({ queryKey: qk.mcp.overview })} />}
      <PageHeader
        title="MCP"
        icon={Plug}
        subtitle="Upstream MCP servers, managed centrally — grant each tool per persona"
        actions={<>
          <Button size="sm" variant="secondary" onClick={() => setBuilding(true)}><Sparkles className="size-3.5" /> Add with agent</Button>
          <Button size="sm" onClick={() => setAdding(true)}><Plus className="size-3.5" /> Add server</Button>
        </>}
      />
      <PageBody className="space-y-4">
        {isLoading && <CenteredSpinner label="loading MCP servers" />}
        {error && <ErrorBanner error={error as Error} />}
        {adding && <AddServerForm onClose={() => setAdding(false)} />}
        {data && data.servers.length === 0 && !adding && (
          <EmptyState icon={<Plug className="size-7" />} title="No MCP servers yet" description="Add a server — its tools become grantable per persona, exposed through the agent-fleet bus." />
        )}
        {data?.servers.map((s) => <ServerCard key={s.name} server={s} personas={data.personas} />)}
      </PageBody>
    </>
  );
}

// Pull code+state out of a pasted redirect — a full URL, a scheme-less
// "127.0.0.1:8765/callback?…", or a bare query string all work. URLSearchParams
// decodes %XX, so the code is handed to the bridge already-decoded.
function parseRedirect(s: string): { code?: string; state?: string } {
  const q = s.trim().includes("?") ? s.trim().slice(s.trim().indexOf("?") + 1) : s.trim();
  const p = new URLSearchParams(q);
  return { code: p.get("code") ?? undefined, state: p.get("state") ?? undefined };
}

function ServerCard({ server, personas }: { server: McpServerOverview; personas: string[] }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [manualOpen, setManualOpen] = useState(false);
  const [pasted, setPasted] = useState("");
  // Key-at-connect servers (e.g. reserve's X-MCP-Key): typed in here, never shown again.
  const missingHeaders = server.missing_headers ?? [];
  const [keyOpen, setKeyOpen] = useState(false);
  const [keyVals, setKeyVals] = useState<Record<string, string>>({});
  const fillKey = useMutation({
    mutationFn: () => BridgeApi.fillMcpHeaders(server.name, keyVals),
    onSuccess: (r) => {
      if (r.error) { toast.error("Connect failed", r.error); return; }
      if (r.missing?.length) { toast.error("Still missing", r.missing.join(", ")); return; }
      toast.success(`${server.name} connected`, "Its tools appear once the server answers.");
      setKeyOpen(false); setKeyVals({});
      qc.invalidateQueries({ queryKey: qk.mcp.overview });
    },
    onError: (e) => toast.error("Connect failed", String(e)),
  });
  const del = useMutation({
    mutationFn: () => BridgeApi.deleteMcpServer(server.name),
    onSuccess: () => { qc.invalidateQueries({ queryKey: qk.mcp.overview }); toast.success(`${server.name} removed`); },
    onError: (e) => toast.error("Delete failed", String(e)),
  });
  const connect = useMutation({
    mutationFn: () => BridgeApi.mcpOAuthStart(server.name),
    onSuccess: (r) => {
      if (r.error) { toast.error("Connect failed", r.error); return; }
      if (r.authorized) { toast.success(`${server.name} already authorized`); qc.invalidateQueries({ queryKey: qk.mcp.overview }); return; }
      if (r.authUrl) { window.open(r.authUrl, "_blank", "noopener,noreferrer"); toast.success("Approve in the new tab", "This page refreshes once connected."); }
    },
    onError: (e) => toast.error("Connect failed", String(e)),
  });
  const startManual = useMutation({
    mutationFn: () => BridgeApi.mcpOAuthStartManual(server.name),
    onSuccess: (r) => {
      if (r.error) { toast.error("Connect failed", r.error); return; }
      if (r.authorized) { toast.success(`${server.name} already authorized`); qc.invalidateQueries({ queryKey: qk.mcp.overview }); return; }
      if (r.authUrl) { window.open(r.authUrl, "_blank", "noopener,noreferrer"); setManualOpen(true); }
    },
    onError: (e) => toast.error("Connect failed", String(e)),
  });
  const finishManual = useMutation({
    mutationFn: () => {
      const { code, state } = parseRedirect(pasted);
      if (!code || !state) throw new Error("Couldn't find code & state in that URL — paste the whole 127.0.0.1 address.");
      return BridgeApi.mcpOAuthFinish(state, code);
    },
    onSuccess: (r) => {
      if (r?.error) { toast.error("Finish failed", r.error); return; }
      toast.success(`${server.name} connected`); setManualOpen(false); setPasted("");
      qc.invalidateQueries({ queryKey: qk.mcp.overview });
    },
    onError: (e) => toast.error("Finish failed", String((e as Error).message ?? e)),
  });
  return (
    <Panel className="p-4 space-y-3 min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-semibold text-fg">{server.name}</span>
        <Pill tone="neutral">{server.transport}</Pill>
        <span className={cn("inline-flex items-center gap-1 text-[11px]", server.connected ? "text-success" : "text-fg-subtle")}>
          <CircleDot className="size-3" /> {server.connected ? `connected · ${server.tools.length} tools` : "not connected"}
        </span>
        {server.auth === "oauth"
          ? <Pill tone={server.oauth_authorized ? "info" : "warning"}>{server.oauth_authorized ? "OAuth authorized" : "OAuth — connect"}</Pill>
          : missingHeaders.length > 0
            ? <Pill tone="warning">needs access key</Pill>
            : server.has_creds && <Pill tone="info">creds set</Pill>}
        {!server.enabled && <Pill tone="warning">disabled</Pill>}
        <span className="ml-auto" />
        {server.auth === "oauth" && (
          <>
            <Button size="sm" variant="secondary" disabled={connect.isPending} onClick={() => connect.mutate()}>
              <Plug className="size-3.5" /> {server.oauth_authorized ? "Reconnect" : "Connect"}
            </Button>
            <button
              className="text-[11px] text-fg-subtle hover:text-fg underline underline-offset-2"
              disabled={startManual.isPending}
              onClick={() => startManual.mutate()}
              title="For providers (Asana, Vercel, …) that reject our callback — approve, then paste the redirected URL back"
            >manually</button>
          </>
        )}
        {server.auth !== "oauth" && missingHeaders.length > 0 && (
          <Button size="sm" variant="secondary" onClick={() => setKeyOpen(true)}>
            <Plug className="size-3.5" /> Connect
          </Button>
        )}
        <span className="text-[11px] text-fg-subtle font-mono truncate max-w-[40%]" title={server.url ?? server.command ?? ""}>{server.url ?? server.command}</span>
        <Button size="icon" variant="ghost" onClick={() => { if (confirm(`Remove MCP server "${server.name}"? Personas lose its tools.`)) del.mutate(); }} aria-label="Delete"><Trash2 className="size-4" /></Button>
      </div>

      {manualOpen && (
        <div className="rounded-md border border-border bg-bg-subtle p-3 space-y-2">
          <div className="text-[11px] text-fg-muted leading-relaxed">
            <b className="text-fg">Manual connect.</b> Approve in the tab that opened. Your browser will then try to load a
            <code className="mx-1 px-1 rounded bg-bg text-fg-muted">127.0.0.1:8765</code>page that <b>won't load — that's expected</b>.
            Copy that full address from the bar and paste it below.
          </div>
          <div className="flex items-center gap-2">
            <input
              value={pasted} onChange={(e) => setPasted(e.target.value)}
              className="flex-1 text-xs font-mono" placeholder="http://127.0.0.1:8765/callback?code=…&state=…"
            />
            <Button size="sm" disabled={!pasted.trim() || finishManual.isPending} loading={finishManual.isPending} onClick={() => finishManual.mutate()}>Finish</Button>
            <Button size="sm" variant="ghost" onClick={() => { setManualOpen(false); setPasted(""); }}>Cancel</Button>
          </div>
        </div>
      )}

      {keyOpen && (
        <div className="rounded-md border border-border bg-bg-subtle p-3 space-y-2">
          <div className="text-[11px] text-fg-muted leading-relaxed">
            <b className="text-fg">Connect.</b> Enter the access key for {server.name}. It is stored on the host and never
            shown again; the server is enabled once every key is set.
          </div>
          {missingHeaders.map((h) => (
            <label key={h} className="flex items-center gap-2 text-xs">
              <span className="w-28 shrink-0 font-mono text-fg-muted">{h}</span>
              <input
                type="password" autoComplete="off" value={keyVals[h] ?? ""}
                onChange={(e) => setKeyVals((v) => ({ ...v, [h]: e.target.value }))}
                className="flex-1 text-xs font-mono" placeholder="access key"
              />
            </label>
          ))}
          <div className="flex items-center gap-2">
            <Button size="sm" disabled={missingHeaders.some((h) => !(keyVals[h] ?? "").trim()) || fillKey.isPending} loading={fillKey.isPending} onClick={() => fillKey.mutate()}>Save &amp; connect</Button>
            <Button size="sm" variant="ghost" onClick={() => { setKeyOpen(false); setKeyVals({}); }}>Cancel</Button>
          </div>
        </div>
      )}

      {server.tools.length === 0 ? (
        <p className="text-[11px] text-fg-subtle">No tools discovered (server not connected). Check the url/command + creds.</p>
      ) : (
        <div className="space-y-1.5">
          <div className="text-[11px] uppercase tracking-wide text-fg-subtle">Access — who can use this server's tools</div>
          {personas.map((p) => (
            <GrantRow key={p} persona={p} server={server} grant={server.grants[p] ?? { mode: "off", tools: [] }} />
          ))}
        </div>
      )}
    </Panel>
  );
}

function GrantRow({ persona, server, grant }: { persona: string; server: McpServerOverview; grant: { mode: McpGrantMode; tools: string[] } }) {
  const qc = useQueryClient();
  const toast = useToast();
  const mut = useMutation({
    mutationFn: (v: { mode: McpGrantMode; tools: string[] }) => BridgeApi.setMcpGrant(persona, server.name, v.mode, v.tools),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.mcp.overview }),
    onError: (e) => toast.error("Grant failed", String(e)),
  });
  const set = (mode: McpGrantMode, tools: string[] = []) => mut.mutate({ mode, tools });
  // Custom with nothing ticked is stored as no grant rows (= Off), so whether the picker
  // is open lives here. Before, choosing Custom saved an empty list, which DELETED the
  // grant and hid the picker (2026-10-02, alfred lost reserve).
  const [picking, setPicking] = useState(false);
  const shown = picking || grant.mode === "custom";
  const activeMode: McpGrantMode = shown ? "custom" : grant.mode;
  const toggleTool = (tool: string) => {
    setPicking(true);   // keep the picker open even after unticking the last tool
    const has = grant.tools.includes(tool);
    set("custom", has ? grant.tools.filter((t) => t !== tool) : [...grant.tools, tool]);
  };
  const choose = (mode: McpGrantMode) => {
    if (mode !== "custom") { setPicking(false); set(mode); return; }
    setPicking(true);
    // From All, start with every tool ticked so choosing Custom never removes access.
    // From Off, just open the picker — nothing is granted until a tool is ticked.
    if (grant.mode === "all") set("custom", server.tools.map((t) => t.name));
  };
  const MODES: { id: McpGrantMode; label: string }[] = [
    { id: "off", label: "Off" }, { id: "all", label: "All" }, { id: "custom", label: "Custom" },
  ];
  return (
    <div className="rounded-md border border-border/60 bg-surface-2/30 px-2.5 py-1.5">
      <div className="flex items-center gap-2">
        <span className="text-xs font-medium text-fg w-24 truncate">{persona}</span>
        <div className="inline-flex rounded-md border border-border overflow-hidden">
          {MODES.map((m) => (
            <button
              key={m.id} disabled={mut.isPending}
              onClick={() => choose(m.id)}
              className={cn("px-2 h-6 text-[11px] transition-colors", activeMode === m.id ? "bg-accent text-white" : "bg-surface text-fg-muted hover:bg-surface-2")}
            >{m.label}</button>
          ))}
        </div>
        {activeMode === "all" && <span className="text-[11px] text-fg-subtle">all {server.tools.length} tools</span>}
        {activeMode === "custom" && <span className="text-[11px] text-fg-subtle">{grant.tools.length} selected{grant.tools.length === 0 ? " — tick the tools to grant" : ""}</span>}
      </div>
      {shown && (
        <div className="flex flex-wrap gap-1 mt-1.5 pl-24">
          {server.tools.map((t) => (
            <button
              key={t.name} disabled={mut.isPending} onClick={() => toggleTool(t.name)} title={t.description}
              className={cn("px-1.5 h-5 rounded text-[10px] font-mono border", grant.tools.includes(t.name) ? "bg-accent/20 border-accent/50 text-fg" : "border-border text-fg-subtle hover:bg-surface-2")}
            >{t.name}</button>
          ))}
        </div>
      )}
    </div>
  );
}

function AddServerForm({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState("");
  const [transport, setTransport] = useState<"http" | "stdio">("http");
  const [url, setUrl] = useState("");
  const [auth, setAuth] = useState<"header" | "oauth" | "none">("header");
  const [headers, setHeaders] = useState("");   // JSON
  const [command, setCommand] = useState("");
  const [args, setArgs] = useState("");          // one per line
  const [env, setEnv] = useState("");            // JSON
  const [stateful, setStateful] = useState(false);
  const save = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = { name: name.trim(), transport, stateful };
      if (transport === "http") {
        body.url = url.trim();
        if (auth === "oauth") body.auth_type = "oauth";
        else if (auth === "header" && headers.trim()) body.headers = JSON.parse(headers);
      } else {
        body.command = command.trim();
        body.args = args.split("\n").map((s) => s.trim()).filter(Boolean);
        if (env.trim()) body.env = JSON.parse(env);
      }
      const r = await BridgeApi.addMcpServer(body);
      if (r.error) throw new Error(r.error);
      return r;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: qk.mcp.overview }); toast.success(`${name} added`); onClose(); },
    onError: (e) => toast.error("Add failed", String((e as Error).message ?? e)),
  });
  return (
    <Panel className="p-4 space-y-3">
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold text-fg">Add MCP server</span>
        <Button size="icon" variant="ghost" className="ml-auto" onClick={onClose} aria-label="Close"><XIcon className="size-4" /></Button>
      </div>
      <label className="text-xs block"><span className="block text-fg-muted mb-0.5">Start from a connector (optional)</span>
        <select
          defaultValue=""
          onChange={(e) => {
            const c = CONNECTOR_CATALOG.find((x) => x.name === e.target.value);
            if (!c) return;
            setTransport("http"); setName(c.name); setUrl(c.url); setAuth(c.auth);
          }}
          className="w-full text-sm h-8"
        >
          <option value="">— pick a known connector, or fill in below —</option>
          {CONNECTOR_CATALOG.map((c) => <option key={c.name} value={c.name}>{c.label}{c.auth === "oauth" ? (c.manual ? " · OAuth (connect manually)" : " · OAuth") : ""}</option>)}
        </select></label>
      <div className="grid sm:grid-cols-2 gap-2">
        <label className="text-xs"><span className="block text-fg-muted mb-0.5">Name (lowercase, no __)</span>
          <input value={name} onChange={(e) => setName(e.target.value)} className="w-full text-sm" placeholder="notion" /></label>
        <label className="text-xs"><span className="block text-fg-muted mb-0.5">Transport</span>
          <select value={transport} onChange={(e) => setTransport(e.target.value as "http" | "stdio")} className="w-full text-sm h-8"><option value="http">http</option><option value="stdio">stdio</option></select></label>
      </div>
      {transport === "http" ? (
        <>
          <label className="text-xs block"><span className="block text-fg-muted mb-0.5">URL</span>
            <input value={url} onChange={(e) => setUrl(e.target.value)} className="w-full text-sm font-mono" placeholder="https://host/mcp" /></label>
          <label className="text-xs block"><span className="block text-fg-muted mb-0.5">Auth</span>
            <select value={auth} onChange={(e) => setAuth(e.target.value as "header" | "oauth" | "none")} className="w-full text-sm h-8">
              <option value="header">Header / token</option>
              <option value="oauth">OAuth (connect after adding)</option>
              <option value="none">None</option>
            </select></label>
          {auth === "header" && (
            <label className="text-xs block"><span className="block text-fg-muted mb-0.5">Headers (JSON, optional — e.g. auth)</span>
              <textarea value={headers} onChange={(e) => setHeaders(e.target.value)} rows={2} className="w-full text-xs font-mono" placeholder='{"Authorization": "Bearer …"}' /></label>
          )}
          {auth === "oauth" && (
            <p className="text-[11px] text-fg-subtle">After adding, click <b>Connect</b> on the server to authorize via the provider — tokens are stored and auto-refreshed by the gateway.</p>
          )}
        </>
      ) : (
        <>
          <label className="text-xs block"><span className="block text-fg-muted mb-0.5">Command</span>
            <input value={command} onChange={(e) => setCommand(e.target.value)} className="w-full text-sm font-mono" placeholder="npx" /></label>
          <label className="text-xs block"><span className="block text-fg-muted mb-0.5">Args (one per line)</span>
            <textarea value={args} onChange={(e) => setArgs(e.target.value)} rows={2} className="w-full text-xs font-mono" placeholder={"-y\n@some/mcp-server"} /></label>
          <label className="text-xs block"><span className="block text-fg-muted mb-0.5">Env (JSON, optional)</span>
            <textarea value={env} onChange={(e) => setEnv(e.target.value)} rows={2} className="w-full text-xs font-mono" placeholder='{"API_KEY": "…"}' /></label>
        </>
      )}
      <label className="flex items-center gap-2 text-xs text-fg-muted">
        <input type="checkbox" checked={stateful} onChange={(e) => setStateful(e.target.checked)} />
        Stateful — give each persona its own session (e.g. a browser like playwright, where state shouldn't be shared)
      </label>
      <div className="flex items-center gap-2">
        <Button size="sm" onClick={() => save.mutate()} loading={save.isPending} disabled={!name.trim() || (transport === "http" ? !url.trim() : !command.trim())}>
          {transport === "http" && auth === "oauth" ? "Add server" : "Add & connect"}
        </Button>
        <span className="text-[11px] text-fg-subtle">
          {transport === "http" && auth === "oauth" ? "Then click Connect to authorize; grant its tools to personas below." : "Connects immediately; grant its tools to personas below."}
        </span>
      </div>
    </Panel>
  );
}
